import { Auth } from 'aws-amplify';
import React, { useState, useEffect, Suspense, ComponentType } from 'react';
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import SplashScreen from './components/SplashScreen/SplashScreen';
import { revertAll } from './redux-toolkit/reducers/userReducer';
import constants, { ROUTES } from './shared/constants';
import { MyTeam } from './components/MyTeam/MyTeam';
import TeamGoals from './components/Goals/TeamGoals';
import MyGoals from './components/Goals/MyGoals';

const Home = React.lazy(() => import('./pages/home/Home'));
const HomePage = React.lazy(() => import('./components/Home/Home'));
const Login = React.lazy(() => import('./pages/login/login'));
const ManageEmployee = React.lazy(() => import('./components/ManageEmployee/ManageEmployee'));
const ManageProjects = React.lazy(() => import('./components/ManageProjects/ManageProjects'));
const MuiDataGrid = React.lazy(() => import('./components/DataGrid/MuiDataGrid'));
const ExceptionReport = React.lazy(() => import('./components/ExceptionReport/ExceptionReport'));
const MonthlyReport = React.lazy(() => import('./components/MonthlyReport/MonthlyReport'));
const Instruction = React.lazy(() => import('./pages/instructions/Instruction'));
const Checklist = React.lazy(() => import('./pages/checklist/Checklist'));
const ComingSoon = React.lazy(() => import('./components/ComingSoon/ComingSoon'));
const Rewards = React.lazy(() => import('./components/Home/Rewards'));
const Staff = React.lazy(() => import('./components/Home/Staff'));
const Blogs = React.lazy(() => import('./components/Home/MainBlogs'));

const tasks = {
    'Work Log': ROUTES.DAILYREPORT,
    'Employees': ROUTES.EMPLOYEE,
    'Projects': ROUTES.PROJECTS,
    'Exception Report': ROUTES.SUMMARY,
    'Monthly Report': ROUTES.MONTHLYREPORT,
    'My Team': ROUTES.TEAMS,
    'Team Goals': ROUTES.TEAM_GOALS,
    'My Goals': ROUTES.MYGOALS,
};

const getAccessibleRoutes = (employeeType: string, showTeamDetails?: boolean) => {
  let accessibleTasks: any[] = [tasks[constants.MAIN_NAV_ITEMS.WORK_LOG], tasks[constants.MAIN_NAV_ITEMS.EMPLOYEES], tasks[constants.MAIN_NAV_ITEMS.MY_GOALS], tasks[constants.MAIN_NAV_ITEMS.TEAM_GOALS]];

  if (employeeType === constants.EMPLOYEE_TYPES.TEAM_LEAD || employeeType === constants.EMPLOYEE_TYPES.PROJECT_MANAGER) {
    accessibleTasks.push(tasks[constants.MAIN_NAV_ITEMS.PROJECTS], tasks[constants.MAIN_NAV_ITEMS.EXCEPTION_REPORTS],tasks[constants.MAIN_NAV_ITEMS.MY_TEAM]);
  }
  if (showTeamDetails && !accessibleTasks.includes(tasks[constants.MAIN_NAV_ITEMS.MY_TEAM])) {
    accessibleTasks.push(tasks[constants.MAIN_NAV_ITEMS.MY_TEAM]);
  }
	if (employeeType === constants.EMPLOYEE_TYPES.ADMIN) {
    accessibleTasks = Object.values(tasks);
  }

  return accessibleTasks;
};


const ProtectedRoute = ({ children, allowedRoutes }: { children: JSX.Element, allowedRoutes: string[] }) => {
    const currentPath = window.location.pathname;
    if (allowedRoutes.includes(currentPath)) {
        return children;
    } else {
        return <Navigate to={ROUTES.HOME} />;
    }
};

function App() {
    const dispatch = useDispatch();
    const [isLogin, setIsLogin] = useState(true);
    const [isLoading, setIsLoading] = useState(true);
		const user = useSelector((state: any) => state.user);
    useEffect(() => {
        Auth.currentAuthenticatedUser()
            .then(() => {
                setIsLogin(true);
                setIsLoading(false);
            })
            .catch(() => {
                setIsLogin(false);
                dispatch(revertAll());
                setIsLoading(false);
            });
    }, [dispatch]);

    const allowedRoutes = getAccessibleRoutes(user.employeeType, user.showTeamDetails);

    const router = createBrowserRouter([
        {
            path: ROUTES.LOGIN,
            element: isLogin ? <Navigate to={ROUTES.HOME} /> : <Login />,
        },
        {
            path: ROUTES.INSTRUCTIONS,
            element: <Instruction />,
        },
        {
            path: ROUTES.CHECKLIST,
            element: <Checklist />
        },
        {
            path: ROUTES.COMINGSOON,
            element: <ComingSoon />
        },
        {
            path: ROUTES.HOME,
            element: isLogin ? <Home /> : <Navigate to={ROUTES.LOGIN} />,
            children: [
                { path: ROUTES.HOME, element: <HomePage /> },
                { path: ROUTES.DAILYREPORT, element: <ProtectedRoute allowedRoutes={allowedRoutes}><MuiDataGrid /></ProtectedRoute> },
                { path: ROUTES.EMPLOYEE, element: <ProtectedRoute allowedRoutes={allowedRoutes}><ManageEmployee /></ProtectedRoute> },
                { path: ROUTES.PROJECTS, element: <ProtectedRoute allowedRoutes={allowedRoutes}><ManageProjects /></ProtectedRoute> },
                { path: ROUTES.SUMMARY, element: <ProtectedRoute allowedRoutes={allowedRoutes}><ExceptionReport /></ProtectedRoute> },
                { path: ROUTES.MONTHLYREPORT, element: <ProtectedRoute allowedRoutes={allowedRoutes}><MonthlyReport /></ProtectedRoute> },
                { path: ROUTES.TEAMS, element: <ProtectedRoute allowedRoutes={allowedRoutes}><MyTeam /></ProtectedRoute> },
                { path: ROUTES.TEAM_GOALS, element: <ProtectedRoute allowedRoutes={allowedRoutes}><TeamGoals /></ProtectedRoute> },
                { path: ROUTES.MYGOALS, element: <ProtectedRoute allowedRoutes={allowedRoutes}><MyGoals /></ProtectedRoute> },
                { path: ROUTES.REWARDS, element: <Rewards /> },
                { path: ROUTES.STAFF, element: <Staff /> },
                {path: ROUTES.BLOGS, element: <Blogs />}
            ]
        }
    ]);

    return (
        <div className='main'>
            {isLoading ? (
                <SplashScreen />
            ) : (
                <Suspense fallback={<SplashScreen />}>
                    <RouterProvider router={router} />
                </Suspense>
            )}
        </div>
    );
}

export default App;
